import React, { useMemo, useState } from 'react';
import { Select, Grid } from 'antd';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ExpandRight,
  ChevronDown as ExpandDown,
} from 'lucide-react';

const { Option: SelectOption } = Select;

/**
 * DataTable — v2 design-system table primitive.
 *
 * Built on TanStack Table v8 (headless). We own the markup so every cell
 * lives in our BEM structure and SCSS. Intentional non-goals: grouping,
 * column resizing, column pinning — add when a feature needs them.
 *
 * Columns shape (standard TanStack + v2 extras):
 *   {
 *     id | accessorKey,
 *     header: string | (ctx) => node,
 *     cell?: (ctx) => node,            // default: renders value as-is
 *     sortable?: boolean,              // enables sort UI (default false)
 *     align?: 'left' | 'center' | 'right',   // text alignment of cell + header
 *     width?: number | string,         // column width
 *     meta?: { ... }                   // passthrough for custom logic
 *   }
 *
 * Expandable rows:
 *   expandable={{
 *     isRowExpandable: (row) => boolean,
 *     renderExpandedRow: (row) => ReactNode,
 *   }}
 *
 * Summary row (renders at the bottom, above pagination):
 *   summaryRow={{
 *     cells: { [columnId]: ReactNode },   // sparse map keyed by column id
 *     labelColumnId: string,              // which column gets the "Totals"
 *     label: string,                      // e.g. "Totals + Averages"
 *   }}
 *
 * Mobile card mode — avoids horizontal scroll on small viewports:
 *   mobileBreakpoint={'md' | 'sm' | 'lg' | 'xl' | 'xxl'}   // AntD key
 *   renderMobileCard={(rowData) => ReactNode}
 * Below the breakpoint DataTable renders a vertical list of consumer-supplied
 * cards. Pagination, summary row, and empty state still apply.
 *
 * Server-side pagination — when the backend owns paging (e.g. URL-param
 * driven tables that refetch per page):
 *   manualPagination={true}
 *   pageIndex={number}                    // 0-based page, externally owned
 *   pageSize={number}                     // still required for display
 *   totalRows={number}                    // server-reported total count
 *   onPaginationChange={({pageIndex, pageSize}) => void}
 * In manual mode DataTable does NOT slice `data` — parent is expected to
 * pass already-paginated rows. Client-side pagination stays the default.
 *
 * Server-side sorting — when the backend owns ordering (e.g. URL-param
 * driven tables that refetch on sort change):
 *   manualSorting={true}
 *   sorting={[{id, desc}]}                // TanStack sort state, externally owned
 *   onSortingChange={(next) => void}      // `next` is `[{id, desc}]`
 * In manual mode the table does NOT re-order `data` — parent supplies rows
 * already in the requested order. Each column still declares `sortable: true`
 * to enable the header sort UI.
 */
const DataTable = ({
  data,
  columns,
  pageSize: initialPageSize = 10,
  pageSizeOptions = [10, 20, 50, 100],
  expandable,
  summaryRow,
  emptyState,
  className = '',
  getRowClassName,
  mobileBreakpoint,
  renderMobileCard,
  // Server-side pagination
  manualPagination = false,
  pageIndex: externalPageIndex,
  totalRows,
  onPaginationChange,
  // Server-side sorting
  manualSorting = false,
  sorting: externalSorting,
  onSortingChange: externalOnSortingChange,
}) => {
  const screens = Grid.useBreakpoint();
  // AntD breakpoints are additive: screens.md is true at md, lg, xl, xxl.
  // "Below md" means screens.md is false — that's our card mode trigger.
  const isMobile = mobileBreakpoint && renderMobileCard
    ? !screens[mobileBreakpoint]
    : false;
  const [internalSorting, setInternalSorting] = useState([]);
  const sorting = manualSorting
    ? (externalSorting != null ? externalSorting : [])
    : internalSorting;
  const setSorting = manualSorting
    ? (updater) => {
        if (!externalOnSortingChange) return;
        const next = typeof updater === 'function' ? updater(sorting) : updater;
        externalOnSortingChange(next);
      }
    : setInternalSorting;
  const [expanded, setExpanded] = useState({});
  // Client-side pagination state (ignored when manualPagination is on — the
  // consumer then owns pageIndex via externalPageIndex + onPaginationChange).
  const [internalPagination, setInternalPagination] = useState({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const pagination = manualPagination
    ? {
        pageIndex: externalPageIndex != null ? externalPageIndex : 0,
        pageSize: initialPageSize,
      }
    : internalPagination;
  const setPagination = manualPagination
    ? (updater) => {
        if (!onPaginationChange) return;
        const next = typeof updater === 'function' ? updater(pagination) : updater;
        onPaginationChange(next);
      }
    : setInternalPagination;

  const enhancedColumns = useMemo(() => {
    if (!expandable) return columns;
    // Prepend an expand/collapse chevron column when expandable is configured.
    const expandColumn = {
      id: '__expand__',
      header: '',
      size: 32,
      meta: { align: 'center', width: 32 },
      cell: ({ row }) => {
        if (!expandable.isRowExpandable || !expandable.isRowExpandable(row.original)) {
          return null;
        }
        const isExpanded = row.getIsExpanded();
        return (
          <button
            type="button"
            className="ds-table__expand-btn"
            aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
            onClick={(e) => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
          >
            {isExpanded
              ? <ExpandDown size={14} strokeWidth={2} />
              : <ExpandRight size={14} strokeWidth={2} />}
          </button>
        );
      },
    };
    return [expandColumn, ...columns];
  }, [columns, expandable]);

  const table = useReactTable({
    data,
    columns: enhancedColumns,
    state: { sorting, expanded, pagination },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    // Manual sorting: skip the client-side re-order — parent supplies rows
    // already in the requested order from the server.
    ...(manualSorting
      ? { manualSorting: true }
      : { getSortedRowModel: getSortedRowModel() }),
    // Manual pagination: skip the client-side slice, supply server-side
    // pageCount so next/prev buttons disable correctly at the edges.
    ...(manualPagination
      ? {
          manualPagination: true,
          pageCount: totalRows != null
            ? Math.max(1, Math.ceil(totalRows / (pagination.pageSize || 1)))
            : -1,
        }
      : {
          getPaginationRowModel: getPaginationRowModel(),
        }),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: expandable
      ? (row) => !!(expandable.isRowExpandable && expandable.isRowExpandable(row.original))
      : undefined,
  });

  const rows = table.getRowModel().rows;
  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;
  const showPager = pageCount > 1;
  // Footer row-count: use server-reported totalRows when manual, otherwise
  // the client-side data length.
  const displayedTotal = manualPagination && totalRows != null ? totalRows : data.length;

  const renderAlign = (align) => {
    if (align === 'right') return 'ds-table__cell--right';
    if (align === 'center') return 'ds-table__cell--center';
    return '';
  };

  // =============================================================================
  // Mobile card render path
  // =============================================================================
  if (isMobile) {
    return (
      <div className={`ds-table ds-table--mobile ${className}`}>
        <div className="ds-table__cards">
          {rows.length === 0 && (
            <div className="ds-table__cards-empty">
              {emptyState || 'No data to display.'}
            </div>
          )}
          {rows.map((row) => {
            const customRowClass = getRowClassName
              ? getRowClassName(row.original) || ''
              : '';
            const canExpand = expandable
              && expandable.isRowExpandable
              && expandable.isRowExpandable(row.original);
            const isExpanded = row.getIsExpanded();

            // Whole-card click target. Skip if the event originated in an
            // interactive child so links / form fields / nested buttons keep
            // their own behavior. `[data-no-expand]` is an escape hatch for
            // consumers (e.g., a card-level dropdown trigger they want to
            // suppress from expansion).
            //
            // Critical: the card itself carries role="button" for a11y, so
            // `closest('[role=button]')` matches the card element. We must
            // only treat NESTED matches (descendants) as "interactive child"
            // — matches equal to currentTarget are the card and should fall
            // through to toggleExpanded().
            const handleCardClick = canExpand
              ? (e) => {
                  const match = e.target.closest(
                    'a, button, input, select, textarea, label, [role="button"], [data-no-expand]'
                  );
                  if (match && match !== e.currentTarget) {
                    return;
                  }
                  row.toggleExpanded();
                }
              : undefined;

            const handleCardKeyDown = canExpand
              ? (e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    row.toggleExpanded();
                  }
                }
              : undefined;

            return (
              <div
                key={row.id}
                className={
                  `ds-table__card ${customRowClass}` +
                  (canExpand ? ' ds-table__card--clickable' : '') +
                  (isExpanded ? ' ds-table__card--expanded' : '')
                }
                onClick={handleCardClick}
                onKeyDown={handleCardKeyDown}
                role={canExpand ? 'button' : undefined}
                tabIndex={canExpand ? 0 : undefined}
                aria-expanded={canExpand ? isExpanded : undefined}
              >
                {canExpand && (
                  <button
                    type="button"
                    className="ds-table__card-expand-btn"
                    aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                    aria-expanded={isExpanded}
                    onClick={(e) => {
                      e.stopPropagation();
                      row.toggleExpanded();
                    }}
                  >
                    {isExpanded
                      ? <ExpandDown size={14} strokeWidth={2} />
                      : <ExpandRight size={14} strokeWidth={2} />}
                  </button>
                )}
                <div
                  className={
                    'ds-table__card-body' +
                    (canExpand ? ' ds-table__card-body--with-expand' : '')
                  }
                >
                  {renderMobileCard(row.original, { row })}
                </div>
                {isExpanded && expandable?.renderExpandedRow && (
                  <div className="ds-table__card-expanded">
                    {expandable.renderExpandedRow(row.original)}
                  </div>
                )}
              </div>
            );
          })}
          {summaryRow && rows.length > 0 && (
            <div className="ds-table__card ds-table__card--summary">
              <div className="ds-table__card-summary-label">
                {summaryRow.label}
              </div>
              <div className="ds-table__card-summary-cells">
                {summaryRow.cells &&
                  Object.entries(summaryRow.cells).map(([key, value]) => {
                    if (value == null || value === '-') return null;
                    const columnDef = columns.find((c) => (c.id || c.accessorKey) === key);
                    const label = typeof columnDef?.header === 'string'
                      ? columnDef.header
                      : key;
                    return (
                      <div key={key} className="ds-table__card-summary-cell">
                        <span className="ds-table__card-summary-cell-label">{label}</span>
                        <span className="ds-table__card-summary-cell-value">{value}</span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {displayedTotal > 0 && (
          <div className="ds-table__pagination">
            <div className="ds-table__page-info">
              {showPager ? (
                <>
                  Page <strong>{pageIndex + 1}</strong> of <strong>{pageCount}</strong>
                  <span className="ds-table__page-total">
                    · {displayedTotal} {displayedTotal === 1 ? 'row' : 'rows'}
                  </span>
                </>
              ) : (
                <>
                  <strong>{displayedTotal}</strong> {displayedTotal === 1 ? 'row' : 'rows'}
                </>
              )}
            </div>

            <div className="ds-table__page-controls">
              {showPager && (
                <div className="ds-table__page-group" role="group" aria-label="Pagination">
                  <button
                    type="button"
                    className="ds-table__page-btn"
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                    aria-label="First page"
                  >
                    <ChevronsLeft size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="ds-table__page-btn"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    aria-label="Previous page"
                  >
                    <ChevronLeft size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="ds-table__page-btn"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    aria-label="Next page"
                  >
                    <ChevronRight size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    className="ds-table__page-btn"
                    onClick={() => table.setPageIndex(pageCount - 1)}
                    disabled={!table.getCanNextPage()}
                    aria-label="Last page"
                  >
                    <ChevronsRight size={14} strokeWidth={2} />
                  </button>
                </div>
              )}

              <div className="ds-table__page-size">
                <span className="ds-table__page-size-label">Show</span>
                <Select
                  size="small"
                  value={pagination.pageSize}
                  onChange={(v) => table.setPageSize(Number(v))}
                  getPopupContainer={(trigger) => trigger.parentNode}
                  popupClassName="mjl-ds"
                  className="ds-table__page-size-select"
                  dropdownMatchSelectWidth={false}
                >
                  {pageSizeOptions.map((n) => (
                    <SelectOption key={n} value={n}>
                      {n}
                    </SelectOption>
                  ))}
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // =============================================================================
  // Desktop table render path
  // =============================================================================
  return (
    <div className={`ds-table ${className}`}>
      <div className="ds-table__scroll">
        <table className="ds-table__inner">
          <thead className="ds-table__thead">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="ds-table__tr">
                {headerGroup.headers.map((header) => {
                  const col = header.column.columnDef;
                  const sortable = col.sortable;
                  const sortDir = header.column.getIsSorted();
                  const alignClass = renderAlign(col.meta?.align || col.align);
                  return (
                    <th
                      key={header.id}
                      className={`ds-table__th ${alignClass}`}
                      style={{ width: col.meta?.width || col.width || col.size }}
                    >
                      {header.isPlaceholder ? null : (
                        <button
                          type="button"
                          className={
                            'ds-table__th-btn' +
                            (sortable ? '' : ' ds-table__th-btn--static')
                          }
                          onClick={sortable ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sortDir === 'asc'
                              ? 'ascending'
                              : sortDir === 'desc'
                                ? 'descending'
                                : 'none'
                          }
                        >
                          <span>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                          </span>
                          {sortable && (
                            <span className="ds-table__sort-icon" aria-hidden="true">
                              {sortDir === 'asc'
                                ? <ChevronUp size={12} strokeWidth={2.5} />
                                : sortDir === 'desc'
                                  ? <ChevronDown size={12} strokeWidth={2.5} />
                                  : <ChevronsUpDown size={12} strokeWidth={1.5} />}
                            </span>
                          )}
                        </button>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="ds-table__tbody">
            {rows.length === 0 && (
              <tr className="ds-table__tr ds-table__tr--empty">
                <td
                  className="ds-table__td ds-table__td--empty"
                  colSpan={enhancedColumns.length}
                >
                  {emptyState || 'No data to display.'}
                </td>
              </tr>
            )}
            {rows.map((row) => {
              const customRowClass = getRowClassName
                ? getRowClassName(row.original) || ''
                : '';
              return (
                <React.Fragment key={row.id}>
                  <tr className={`ds-table__tr ${customRowClass}`}>
                    {row.getVisibleCells().map((cell) => {
                      const col = cell.column.columnDef;
                      const alignClass = renderAlign(col.meta?.align || col.align);
                      return (
                        <td key={cell.id} className={`ds-table__td ${alignClass}`}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                  {row.getIsExpanded() && expandable?.renderExpandedRow && (
                    <tr className="ds-table__tr ds-table__tr--expanded">
                      <td className="ds-table__td" colSpan={row.getVisibleCells().length}>
                        {expandable.renderExpandedRow(row.original)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
          {summaryRow && rows.length > 0 && (
            <tfoot className="ds-table__tfoot">
              <tr className="ds-table__tr ds-table__tr--summary">
                {table.getVisibleLeafColumns().map((col) => {
                  const def = col.columnDef;
                  const alignClass = renderAlign(def.meta?.align || def.align);
                  const isLabelColumn = def.id === summaryRow.labelColumnId || col.id === summaryRow.labelColumnId;
                  const cellContent = isLabelColumn
                    ? summaryRow.label
                    : (summaryRow.cells && (summaryRow.cells[def.id] ?? summaryRow.cells[col.id])) ?? null;
                  return (
                    <td
                      key={col.id}
                      className={`ds-table__td ds-table__td--summary ${alignClass}`}
                    >
                      {cellContent}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {displayedTotal > 0 && (
        <div className="ds-table__pagination">
          <div className="ds-table__page-info">
            {showPager ? (
              <>
                Page <strong>{pageIndex + 1}</strong> of <strong>{pageCount}</strong>
                <span className="ds-table__page-total">
                  · {displayedTotal} {displayedTotal === 1 ? 'row' : 'rows'}
                </span>
              </>
            ) : (
              <>
                <strong>{displayedTotal}</strong> {displayedTotal === 1 ? 'row' : 'rows'}
              </>
            )}
          </div>

          <div className="ds-table__page-controls">
            {showPager && (
              <div className="ds-table__page-group" role="group" aria-label="Pagination">
                <button
                  type="button"
                  className="ds-table__page-btn"
                  onClick={() => table.setPageIndex(0)}
                  disabled={!table.getCanPreviousPage()}
                  aria-label="First page"
                >
                  <ChevronsLeft size={14} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="ds-table__page-btn"
                  onClick={() => table.previousPage()}
                  disabled={!table.getCanPreviousPage()}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={14} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="ds-table__page-btn"
                  onClick={() => table.nextPage()}
                  disabled={!table.getCanNextPage()}
                  aria-label="Next page"
                >
                  <ChevronRight size={14} strokeWidth={2} />
                </button>
                <button
                  type="button"
                  className="ds-table__page-btn"
                  onClick={() => table.setPageIndex(pageCount - 1)}
                  disabled={!table.getCanNextPage()}
                  aria-label="Last page"
                >
                  <ChevronsRight size={14} strokeWidth={2} />
                </button>
              </div>
            )}

            <div className="ds-table__page-size">
              <span className="ds-table__page-size-label">Show</span>
              <Select
                size="small"
                value={pagination.pageSize}
                onChange={(v) => table.setPageSize(Number(v))}
                getPopupContainer={(trigger) => trigger.parentNode}
                popupClassName="mjl-ds"
                className="ds-table__page-size-select"
                dropdownMatchSelectWidth={false}
              >
                {pageSizeOptions.map((n) => (
                  <SelectOption key={n} value={n}>
                    {n}
                  </SelectOption>
                ))}
              </Select>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
